export interface BrandSignatureFrameSettings {
  enabled: boolean;
  showRecipientName: boolean;
  showRecipientEmail: boolean;
  showTimestamp: boolean;
}

export const DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS: BrandSignatureFrameSettings = {
  enabled: false,
  showRecipientName: false,
  showRecipientEmail: false,
  showTimestamp: false,
};

export interface CreateBrandPayload {
  name: string;
  isDefault: boolean;
  logoDataUrl?: string;
  emailDisplayName?: string;
  emailBackgroundColor: string;
  primaryButtonColor: string;
  primaryButtonTextColor: string;
  showPlatformBranding: boolean;
  signatureFrame: BrandSignatureFrameSettings;
}

export type UpdateBrandPayload = Partial<Omit<CreateBrandPayload, 'logoDataUrl'>> & {
  logoDataUrl?: string | null;
};

export interface BrandSummary extends CreateBrandPayload {
  id: string;
  brandId: string;
  createdAt: string;
  updatedAt: string;
}

export interface BrandListResponse {
  total: number;
  items: BrandSummary[];
}

export const EMAIL_TEMPLATE_TYPES = [
  'SignatureRequest',
  'RecipientSignsDocument',
  'DocumentCompletedSingleSigner',
  'DocumentCompletedMultipleSigners',
  'DocumentDeclined',
  'DocumentRevoked',
  'AutoReminder',
  'ManualReminder',
  'OneDayBeforeDocumentExpiration',
  'DocumentExpired',
  'DocumentDeleted',
  'NewDocumentNotificationForCc',
] as const;

export type EmailTemplateType = (typeof EMAIL_TEMPLATE_TYPES)[number];
export type EmailTemplateStatus = 'Default' | 'Customized';
export type EmailTemplateRecipientType = 'Signer' | 'Sender' | 'Cc';

export const EMAIL_TEMPLATE_LABELS: Record<EmailTemplateType, string> = {
  SignatureRequest: 'Signature Request',
  RecipientSignsDocument: 'Recipient Signs a Document',
  DocumentCompletedSingleSigner: 'Document Completed (Single Signer)',
  DocumentCompletedMultipleSigners: 'Document Completed (Multiple Signers)',
  DocumentDeclined: 'Document Declined',
  DocumentRevoked: 'Document Revoked',
  AutoReminder: 'Auto Reminder',
  ManualReminder: 'Manual Reminder',
  OneDayBeforeDocumentExpiration: 'One Day Before Document Expiration',
  DocumentExpired: 'Document Expired',
  DocumentDeleted: 'Document Deleted',
  NewDocumentNotificationForCc: 'New Document Notification for CC',
};

export interface BrandEmailTemplateListItem {
  emailTemplateType: EmailTemplateType;
  displayName: string;
  status: EmailTemplateStatus;
  lastModified: string | null;
}

export interface BrandEmailTemplateListResponse {
  total: number;
  items: BrandEmailTemplateListItem[];
}

export interface GetBrandEmailTemplateDetailsPayload {
  emailTemplateType: EmailTemplateType;
  locale: string;
  isInitialLoad: boolean;
}

export interface EmailTemplateDetails {
  emailTemplateType: EmailTemplateType;
  recipientType: EmailTemplateRecipientType;
  language: string;
  emailSubject: string;
  htmlBody: string;
  plainTextBody: string | null;
  status: EmailTemplateStatus;
}

export interface BrandEmailTemplateDetailsResponse {
  emailTemplateDetailsList: EmailTemplateDetails[];
  recipientTypes: EmailTemplateRecipientType[];
  editedLanguages: string[];
}

export interface UpdateBrandEmailTemplateDetailsPayload {
  emailTemplateType: EmailTemplateType;
  recipientType: EmailTemplateRecipientType;
  locale: string;
  emailSubject: string;
  htmlBody: string;
}

export interface ResetBrandEmailTemplateDetailsPayload {
  emailTemplateType: EmailTemplateType;
  recipientType: EmailTemplateRecipientType;
  locale: string;
}
